import { useParams } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { Skeleton } from '@/components/States'
import { NotFoundScreen } from '@/features/system/NotFoundScreen'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, toMajor } from '@/lib/money'
import checkout from '@/app/layouts/CheckoutLayout.module.css'
import { Plastic } from './Plastic'

const CONTROLS = [
  { icon: 'ac_unit', title: 'Freeze this card', body: 'Instant and reversible' },
  { icon: 'tune', title: 'Spending limits', body: 'Per transaction and per month' },
  { icon: 'language', title: 'Online payments', body: 'On' },
  { icon: 'local_atm', title: 'ATM withdrawals', body: 'On · 2% after $200 a month' },
  { icon: 'contactless', title: 'Apple Pay & Google Pay', body: 'Added to this device' },
  { icon: 'sync', title: 'Replace card', body: 'Lost, stolen or damaged' },
]

const KIND: Record<string, string> = {
  physical: 'Physical card',
  virtual: 'Virtual card',
  agent: 'Agent card',
}

export function CardDetailScreen() {
  const { id } = useParams()
  const repository = useRepository()
  const cards = useQuery(() => repository.listCards(), [repository])

  if (cards.status === 'loading') return <Skeleton height={360} />
  const card = cards.data?.find((item) => item.id === id)
  if (!card) return <NotFoundScreen />

  const used = toMajor(card.spentThisMonth) / toMajor(card.monthlyLimit)

  return (
    <>
      <PageHeader
        title={`${card.label} ••${card.last4}`}
        subtitle={`${KIND[card.kind]} · ${card.network === 'mastercard' ? 'Mastercard' : 'Visa'} · expires ${card.expiry}`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="visibility">
              Show details
            </Button>
            <Button variant="primary" size="md" iconStart="add" to="/top-up">
              Top up
            </Button>
          </>
        }
      />

      <BentoGrid>
        <BentoSpan span={2}>
          <Plastic card={card} />
        </BentoSpan>

        <BentoSpan span={2}>
          <Stack gap={3}>
            <Card tone="light">
              <Card.Header>
                <Text variant="caption" weight="semibold">
                  This month
                </Text>
                <Pill tone={card.status === 'active' ? 'positive' : 'warning'}>{card.status}</Pill>
              </Card.Header>
              <Stack gap={3} className="pb-mt-4">
                <Text as="p" variant="title" weight="bold" numeric>
                  {formatMoney(card.spentThisMonth)}
                </Text>
                <Meter value={used} label="Monthly limit used" />
                <Text variant="caption" tone="secondary" numeric>
                  of {formatMoney(card.monthlyLimit, { compactDecimals: true })} monthly limit
                </Text>
              </Stack>
            </Card>

            <Card tone="dark">
              <Stack direction="row" align="center" gap={3}>
                <Icon name="redeem" size={20} />
                <Stack grow gap={0}>
                  <Text variant="caption" weight="semibold" tone="onDark">
                    0.5% back on everything
                  </Text>
                  <Text variant="eyebrow" tone="onDarkMuted">
                    $600 monthly cap · paid in cash
                  </Text>
                </Stack>
                <Button variant="accentDim" size="sm" to="/cashback">
                  See
                </Button>
              </Stack>
            </Card>
          </Stack>
        </BentoSpan>

        <BentoSpan span={4}>
          <Card tone="light">
            <Text variant="caption" weight="semibold">
              Controls
            </Text>
            <div className={`${checkout.reasons} pb-mt-2`}>
              {CONTROLS.map((control) => (
                <button
                  key={control.title}
                  type="button"
                  className={checkout.reason}
                  style={{ width: '100%', textAlign: 'left' }}
                >
                  <Icon name={control.icon} size={20} />
                  <Stack grow gap={1}>
                    <Text variant="caption" weight="semibold">
                      {control.title}
                    </Text>
                    <Text variant="eyebrow" tone="secondary">
                      {control.body}
                    </Text>
                  </Stack>
                  <Icon name="chevron_right" size={18} />
                </button>
              ))}
            </div>
          </Card>
        </BentoSpan>
      </BentoGrid>
    </>
  )
}
